import { useMemo, useRef, type FC } from 'react';
import { useIntl } from 'react-intl';

import {
  redirectToLogin,
  useLoggedoutAction,
} from '@xing-com/crate-entity-pages-common';
import { usePageContext } from '@xing-com/crate-entity-pages-entity-pages-shared-context-entity-page-context';
import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import { ShareTabOmView } from '@xing-com/crate-social-share';
import { IconMore } from '@xing-com/icons';
import { trackClickHeaderMoreAction } from '@xing-com/crate-entity-pages-common/src/tracking'; // eslint-disable-line
import { Menu } from '@xing-com/menu';
import { usePopOver } from '@xing-com/pop-over';

import { useHeaderContext } from '../../../hooks/use-header-context/use-header-context';

import * as Styled from './header-more-actions.styles';
import { List } from './list';

type HeaderMoreActionsProps = {
  smallOnWide?: boolean;
};

export const HeaderMoreActions: FC<HeaderMoreActionsProps> = ({
  smallOnWide,
}) => {
  const popOver = usePopOver();
  const { isLoggedIn } = useLoginState();
  const { pageContext } = usePageContext();
  const { formatMessage } = useIntl();
  const { moduleType } = useHeaderContext();

  const omViewTrigger = useRef<() => void | undefined>();

  const moreButtonClick = (isEditor: boolean) => {
    popOver.handleShow();
    trackClickHeaderMoreAction({ isEditor });
  };

  const handleOmViewTrigger = () => omViewTrigger.current?.();

  const handleShare = () => {
    if (!isLoggedIn) {
      redirectToLogin('shareEntityPage');
    } else {
      popOver.handleHide();
      handleOmViewTrigger();
    }
  };

  const page = useMemo(() => {
    if (!moduleType) return 'entity_pages/overview';

    const moduleTitle = formatMessage({
      id: `EP_${moduleType.toUpperCase()}_HEADLINE`,
      defaultMessage: `EP_${moduleType.toUpperCase()}_HEADLINE`,
    });

    const slug = moduleTitle
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/(^_|_$)/g, '');
    return `entity_pages/subpage/${slug}`;
  }, [moduleType]);

  useLoggedoutAction(handleOmViewTrigger, 'shareEntityPage');

  return (
    <Styled.Wrapper>
      <Styled.Button
        variant="tertiary"
        data-testid="MODAL_TOGGLE"
        onClick={() => moreButtonClick(pageContext.isEditor)}
        innerRef={popOver.triggerRef}
        size="medium"
        sizeWide={smallOnWide ? 'small' : undefined}
        aria-label={formatMessage({
          id: 'ARTICLES_ARTICLE_VIEWER_ARIA_LABEL_BUTTON_MORE',
        })}
        aria-expanded={popOver.show}
      >
        <IconMore width={16} height={16} />
      </Styled.Button>
      <ShareTabOmView
        url={pageContext.url ?? ''}
        propChannel="wbm/EntityPages"
        entryPoint="entity_page_social_share"
        verb="SHARE"
        interactionTargetUrn={pageContext.globalId}
        trigger={(trigger) => {
          omViewTrigger.current = trigger;

          return (
            <Menu
              onOutsideClick={popOver.handleHide}
              triggerRef={popOver.triggerRef}
              show={popOver.show}
              isInFlow={true}
            >
              <List sharePage={handleShare} closePopUp={popOver.handleHide} />
            </Menu>
          );
        }}
        backendTrackingMetaData={{ page, entryPoint: 'entity_pages' }}
      />
    </Styled.Wrapper>
  );
};
